import React, { useMemo, useRef } from 'react';
import { uniqueId } from 'lodash';
import type { ValidIconName } from './Icon';
import { Icon } from './Icon';
import type { ClassNameProp } from '../common-types';

type SwitchItemProps = ClassNameProp & {
  readonly active?: boolean;
  readonly condensed?: boolean;
  readonly disabled?: boolean;
  readonly icon?: ValidIconName;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly name: string;
};

function SwitchItem({
  active = false,
  condensed = false,
  disabled = false,
  label,
  value,
  onChange,
  icon,
  name,
  className
}: Readonly<SwitchItemProps>): React.JSX.Element {
  const id = useRef(uniqueId('switch-'));
  const classes = useMemo(
    () =>
      (
        (className || '') +
        ' ehs-switch-item ' +
        (active ? 'active ' : '') +
        (disabled ? 'disabled ' : '') +
        (condensed ? 'condensed ' : '') +
        (icon ? 'has-icon' : '')
      ).trim(),
    [active, className, condensed, disabled, icon]
  );

  const iconPart = useMemo(
    () => icon && <Icon iconName={icon} aria-label={label} />,
    [icon, label]
  );

  const title = useMemo(() => icon && label, [icon, label]);
  return (
    <label htmlFor={id.current} className={classes} title={title}>
      <input
        type="radio"
        id={id.current}
        checked={active}
        aria-checked={active}
        aria-disabled={disabled}
        disabled={disabled}
        onChange={() => onChange(value)}
        name={name}
      />
      {iconPart}
      {!icon && <div className="text">{label}</div>}
    </label>
  );
}
type SwitchItemPropsReduced = Omit<SwitchItemProps, 'onChange' | 'name'>;
type SwitchProps = ClassNameProp & {
  readonly condensed?: boolean;
  readonly disabled?: boolean;
  readonly stretch?: boolean;
  readonly value?: string;
  readonly onChange: (value: string) => void;
  readonly elements: Array<SwitchItemPropsReduced>;
};

/**
 * This Component coresponds to this SHUI one https://shui.siemens-healthineers.com/styleguide/components/switch
 * Additionally this component tries to be a radiogroup which the SHUI switch component does not try to be. This manly affects WCAG and to be more precisely keyboard navigation.
 *
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/roles/radiogroup_role
 *
 * @param condensed setting this reduces the padding
 * @param disabled disables all elements
 * @param stretch stretches the component to 100%
 * @param value the value
 * @param onChange the onChange handler
 * @param elements the elements that will be displayed
 * @param className css class
 * @param props leftover {@link SwitchProps}
 */
function Switch({
  condensed = false,
  disabled = false,
  stretch = false,
  value,
  onChange,
  elements,
  className,
  ...props
}: Readonly<SwitchProps>): React.JSX.Element {
  const name = useRef(uniqueId('ehs-switch'));
  const renderedChildren = useMemo(() => {
    return elements.map(e => (
      <SwitchItem
        key={e.value}
        {...e}
        disabled={e.disabled ?? disabled}
        condensed={e.condensed ?? condensed}
        active={value == e.value}
        onChange={onChange}
        name={name.current}
      />
    ));
  }, [elements, value, onChange, disabled, condensed]);
  const classes = useMemo(
    () =>
      ((className || '') + ' ehs-switch ' + (stretch ? 'stretch' : '')).trim(),
    [className, stretch]
  );
  return (
    <div {...props} className={classes} role="radiogroup">
      {renderedChildren}
    </div>
  );
}

export { SwitchItem, Switch };
export type { SwitchProps, SwitchItemPropsReduced };
